import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import classnames from 'classnames';
import { ConnectedPaxHeader } from './FragmentHeader/PaxHeader';
import { Fragment } from './Fragment';
import { FragmentBody } from './FragmentBody';
import { ConnectedPaxSelectorContent, TrainConnectedPaxSelectorContent } from '../../PaxSelector';
import { CMS, ABUtils, isAgeSelectorEnabled, isTrainsRoundTripDiscountCard, withPageContext } from '../../../utils';
import { NextButton } from './NextButton';
import { FlightClassSelector } from '../../FlightClassSelector/FlightClassSelectorMobile';
import { SlideType, TripTypeProp, TripType } from '../../../models';
import { SlideGroupContext } from '../../../components/SlideManager';
import { FieldHeaderPrisma } from './FragmentHeader';
import { ButtonCTA } from './ButtonCTA';
import { isTrainsProductActive } from '../../../redux/currentSearch/selectors';
import { INFANT_MAX_AGE } from '../../PaxSelector/TrainContent';

const PaxHeaderContent = ({ isCssVariationMobile }) => {
    if (!isCssVariationMobile) {
        return <ConnectedPaxHeader />;
    }
    return <FieldHeaderPrisma textKey="flightsManager.selection.pax.title" />;
};

const shouldShowDiscountCards = ({ isTrainsProduct, railsDiscountCards, tripType }) => {
    const isRailsRoundTripDiscountCard = isTrainsRoundTripDiscountCard({ isTrainsProduct, tripType });
    const isOneWay = tripType === TripType.ONE_WAY;

    return isTrainsProduct && railsDiscountCards && (isOneWay || isRailsRoundTripDiscountCard);
};

const PaxBodyContent = ({
    isCssVariationMobile,
    isTrainsProduct,
    paxClassNames,
    hasSubmittedPax,
    showDiscountCards,
    separatorClassNames,
}) => (
    <FragmentBody padding={!isCssVariationMobile}>
        {!isCssVariationMobile && (
            <div className="odf-text-soft odf-text-left odf-text-xl odf-space-outer-bottom-m">
                <CMS id="flightsManager.selection.pax.title" />
            </div>
        )}
        {isTrainsProduct ? (
            <div className={paxClassNames}>
                <TrainConnectedPaxSelectorContent hasSubmittedPax={hasSubmittedPax} />
            </div>
        ) : (
            <div className={paxClassNames}>
                <ConnectedPaxSelectorContent />
            </div>
        )}
        <div className={separatorClassNames} />
        {isCssVariationMobile && <div className="odf-separator-large" />}
        {!isTrainsProduct && <FlightClassSelector />}
        {showDiscountCards && (
            <div id="train_discount_cards" className="shell-integration_portal" data-testid="train-discount-cards-container" />
        )}
    </FragmentBody>
);

const PaxBottomContent = ({ showButtonCTA, hasSubmittedPax, hasTrainPaxErrors, isHotelOptionEnabled, tripType, setHasSubmittedPax }) => {
    if (showButtonCTA) {
        return (
            <div className="prisma-elevation-1 odf-space-inner-bottom-m prisma-responsive-space-horizontal safari-extra-padding-bottom-lg">
                <ButtonCTA
                    isHotelOptionEnabled={isHotelOptionEnabled}
                    isSearchFragmentPage
                    tripType={tripType}
                    buttonWrapperClassNames="odf-space-outer-top-m"
                    secondaryButtonClassNames="odf-space-outer-top-s"
                    position="from-pax-"
                />
            </div>
        );
    }
    return (
        <NextButton
            testId="pax-next-btn"
            disabled={hasSubmittedPax && hasTrainPaxErrors}
            hasErrorValidation={hasTrainPaxErrors}
            showErrorValidation={() => setHasSubmittedPax(true)}
        />
    );
};

export const PaxFragment = ({ isHotelOptionEnabled, tripType, trainPaxAges, trainPax, hasSubmittedPax, setHasSubmittedPax }) => {
    const [showButtonCTA, setShowButtonCTA] = useState(false);
    const { isOpenedWithEmptyFields, getCurrent } = useContext(SlideGroupContext);
    const { isCssVariationMobile, isReduceSearchFlow, railsDiscountCards } = ABUtils.getABs();
    const isTrainsProduct = useSelector((state) => isTrainsProductActive(state));
    const isAgeSelectorActive = isTrainsProduct && isAgeSelectorEnabled();
    const currentSlideGroup = getCurrent();
    const isDone = currentSlideGroup.history.indexOf(SlideType.PAX) > -1 || !isOpenedWithEmptyFields;
    const youthAgesFilled = trainPaxAges.youthAges.every(({ age }) => age != null);
    const childrenAgesFilled = trainPaxAges.childrenAges.every(({ age }) => age != null);
    const numAdults = trainPax.adults + trainPax.seniors + trainPax.youth;
    const numInfants = trainPaxAges.childrenAges.filter((childrenAge) => childrenAge.age !== undefined && childrenAge.age <= INFANT_MAX_AGE).length;
    const totalTrainPax = trainPax.adults + trainPax.seniors + trainPax.youth + trainPax.children + (trainPax.infants || 0);
    const isInfantsGreaterThanAdults = numInfants > numAdults;
    const areAgesMissing = !youthAgesFilled || !childrenAgesFilled;
    const showDiscountCards = shouldShowDiscountCards({ isTrainsProduct, railsDiscountCards, tripType });
    const hasTrainPaxErrors =
        isTrainsProduct &&
        (totalTrainPax === 0 ||
            (isAgeSelectorActive && (areAgesMissing || isInfantsGreaterThanAdults)));
    const paxClassNames = classnames({
        'odf-space-inner-bottom-m': true,
        'prisma-responsive-space-horizontal': isCssVariationMobile,
    });
    const separatorClassNames = classnames({
        'odf-separator-large': isCssVariationMobile,
        'odf-stroke-primary odf-space-outer-bottom-m': !isCssVariationMobile,
    });

    const onEntering = () => {
        if (isReduceSearchFlow) {
            const hasEmptySlides = Array.from(currentSlideGroup.slides.values()).some((slide) => slide.isEmpty);
            setShowButtonCTA(!hasEmptySlides);
        }
    };
    return (
        <Fragment
            id={SlideType.PAX}
            isDone={isDone}
            isEmpty={false}
            setValue={() => {}}
            onEntered={onEntering}
        >
            {() => (
                <>
                    <PaxHeaderContent isCssVariationMobile={isCssVariationMobile} />
                    <PaxBodyContent
                        isCssVariationMobile={isCssVariationMobile}
                        isTrainsProduct={isTrainsProduct}
                        paxClassNames={paxClassNames}
                        hasSubmittedPax={hasSubmittedPax}
                        showDiscountCards={showDiscountCards}
                        separatorClassNames={separatorClassNames}
                    />
                    <PaxBottomContent
                        showButtonCTA={showButtonCTA}
                        hasSubmittedPax={hasSubmittedPax}
                        hasTrainPaxErrors={hasTrainPaxErrors}
                        isHotelOptionEnabled={isHotelOptionEnabled}
                        tripType={tripType}
                        setHasSubmittedPax={setHasSubmittedPax}
                    />
                </>
            )}
        </Fragment>
    );
};

PaxFragment.propTypes = {
    isHotelOptionEnabled: PropTypes.bool,
    tripType: TripTypeProp.isRequired,
    hasSubmittedPax: PropTypes.bool,
    setHasSubmittedPax: PropTypes.func,
};

PaxFragment.defaultProps = {
    isHotelOptionEnabled: true,
    hasSubmittedPax: false,
    setHasSubmittedPax: () => {},
};


const mapStateToProps = ({ currentSearch }) => ({
    tripType: currentSearch.tripType,
    trainPaxAges: currentSearch.trainPaxAges,
    trainPax: currentSearch.trainPax,
});

export const ConnectedPaxFragment = connect(mapStateToProps, {})(withPageContext(PaxFragment));
